'use client';
import PropTypes from 'prop-types';
import warning from 'tiny-warning';
import { SWAN_DEPRECATED_ICONS, SWAN_ICON_KEY_MAP, getSwanIconUrl } from '../../../core/utilities/manifest-icons.utils.js';
import { createComponent } from '../core/create-component.js';
import { useSwanContext } from '../../contexts/swan-provider/swan-provider.context.js';
import { swanStyleObjectFitValues, swanStyleObjectFit } from '../../../core/utilities/swan-style.utils.js';
import { deprecatedPropValues } from '../core/deprecated-prop-values.js';
import { SWAN_STYLE_KEY_MAP } from '../head/stylesheets.component.js';

function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
const propTypes = {
    /**
   * The type of icon to show.
   */ iconType: deprecatedPropValues(PropTypes.oneOf(Object.keys(SWAN_ICON_KEY_MAP)), SWAN_DEPRECATED_ICONS, 'See alternatives on the SWAN docsite.'),
    /**
   * The size of the icon.
   * One of: "standard", "10p", "16p", "20p", "24p", "28p", "32p", "40p", "48p", "60p"
   * Size "10p" is deprecated. Use "16p" size instead.
   * Size "28p", "32p", "40p", "48p" and "60p" are deprecated. Use "standard" size instead.
   */ size: deprecatedPropValues(PropTypes.oneOf([
        'standard',
        '10p',
        '16p',
        '20p',
        '24p',
        '28p',
        '32p',
        '40p',
        '48p',
        '60p'
    ]), [
        '10p',
        '28p',
        '32p',
        '40p',
        '48p',
        '60p'
    ], 'Use 16p instead of 10p and use standard instead of 28p, 32p, 40p, 48p and 60p.'),
    /**
   * The visual style of the icon.
   * One of: "white", "standard", "error", "warning", "success".
   * @default standard
   */ skin: PropTypes.oneOf([
        'standard',
        'white',
        'error',
        'warning',
        'success'
    ]),
    /**
   * Sets the resizing behaviour of the image.
   * One of: "cover", "contain", "none", "unset", "fill"
   */ objectFit: PropTypes.oneOf(swanStyleObjectFitValues)
};
const Icon = createComponent({
    displayName: 'Icon',
    root: 'img',
    propTypes,
    requiredStyleKeys: SWAN_STYLE_KEY_MAP.icon,
    propConfig: {
        derivedProps (props) {
            const { swanPathType, swanBaseUrl } = useSwanContext();
            const isCustom = !!props.src;
            const validIconTypes = Object.keys(SWAN_ICON_KEY_MAP);
            const hasValidIconType = Boolean(props.iconType && validIconTypes.includes(props.iconType));
            warning(!(!props.src && !hasValidIconType), 'Icon: Either iconType is missing or invalid or if you are trying to use it as custom icon the `src` has not been passed');
            let iconUrl = '';
            if (props.iconType && hasValidIconType) {
                iconUrl = getSwanIconUrl(props.iconType, swanPathType, swanBaseUrl);
            }
            return {
                src: props.src,
                maskImage: iconUrl ? `url(${iconUrl})` : undefined,
                isCustom,
                hasValidIconType: hasValidIconType
            };
        },
        processProps (props, derivedProps) {
            const { maskImage, src } = derivedProps;
            return _object_spread_props(_object_spread({}, props), {
                style: _object_spread_props(_object_spread({}, props.style), {
                    maskImage,
                    WebkitMaskImage: maskImage
                }),
                // use a blank img for the src since we're using maskImage instead
                src: src ? src : maskImage ? 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==' : undefined,
                // role: props.role || !props.alt ? 'presentation' : undefined,
                alt: props.alt || ''
            });
        },
        classNames ({ size, skin = 'standard', iconType, objectFit }, derivedProps) {
            const classNames = [
                'swan-icon',
                'swan-icon-mask'
            ];
            const objectFitClassName = swanStyleObjectFit(objectFit);
            if (objectFitClassName) classNames.push(objectFitClassName);
            if (size) classNames.push(`swan-icon-size-${size}`);
            if (skin) classNames.push(`swan-icon-skin-${skin}`);
            if (derivedProps.isCustom) {
                classNames.push('swan-icon-custom');
            } else {
                derivedProps.hasValidIconType && classNames.push(`swan-icon-type-${iconType}`);
            }
            return classNames;
        }
    }
});

export { Icon };
